import type {
  HistoryPoint,
  LiveUpdate,
  MailboxLevel,
  MailboxList,
  MailboxRow,
  MailboxStatus,
} from '../types/mailMonitor';

/** One visual state of a row: colour family, words and icon name, never colour alone. */
export interface LevelLook {
  key: MailboxLevel | MailboxStatus;
  label: string;
  tone: 'success' | 'info' | 'warning' | 'danger' | 'neutral';
  icon: 'check' | 'mail' | 'clock' | 'alert' | 'lock' | 'key' | 'off' | 'pending' | 'error';
  hint?: string;
}

const LEVELS: Record<MailboxLevel, LevelLook> = {
  CLEAR: { key: 'CLEAR', label: 'Clear', tone: 'success', icon: 'check' },
  NEW: { key: 'NEW', label: 'New mail', tone: 'info', icon: 'mail' },
  AGEING: { key: 'AGEING', label: 'Ageing', tone: 'warning', icon: 'clock' },
  OVERDUE: { key: 'OVERDUE', label: 'Overdue', tone: 'danger', icon: 'alert' },
};

const STATUSES: Partial<Record<MailboxStatus, LevelLook>> = {
  AUTH_FAILED: {
    key: 'AUTH_FAILED',
    label: 'Login failed',
    tone: 'neutral',
    icon: 'lock',
    hint: 'Password changed? Replace it in Settings.',
  },
  ERROR: { key: 'ERROR', label: 'Check failed', tone: 'neutral', icon: 'error' },
  NO_SECRET: { key: 'NO_SECRET', label: 'No password', tone: 'neutral', icon: 'key', hint: 'Add the password in Settings.' },
  DISABLED: { key: 'DISABLED', label: 'Off', tone: 'neutral', icon: 'off' },
  PENDING: { key: 'PENDING', label: 'Waiting for first check', tone: 'neutral', icon: 'pending' },
};

export const lookOf = (row: Pick<MailboxRow, 'status' | 'level'>): LevelLook => {
  if (row.status === 'OK' && row.level) return LEVELS[row.level];
  return STATUSES[row.status] ?? STATUSES.PENDING!;
};

const MINUTE = 60_000;

export const minutesSince = (iso: string | null, now: number): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now - t) / MINUTE));
};

/** "6 h 32 m", "45 m", "2 d 3 h"; "—" when unknown. */
export const ageText = (iso: string | null, now: number): string => {
  const m = minutesSince(iso, now);
  if (m === null) return '—';
  if (m < 1) return 'just now';
  if (m < 60) return `${m} m`;
  if (m < 24 * 60) return `${Math.floor(m / 60)} h ${m % 60} m`;
  return `${Math.floor(m / (24 * 60))} d ${Math.floor(m / 60) % 24} h`;
};

/** "10:42" today, "6 Oct 10:42" otherwise, in the browser's time zone. */
export const clockText = (iso: string | null, now: number): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const sameDay = new Date(now).toDateString() === d.toDateString();
  return sameDay ? time : `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${time}`;
};

/** "6 Oct 2026, 11:26" in the browser's time zone; "—" when unknown. */
export const dateTimeText = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${date}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

/** "1.2 MB", "340 KB", "900 B". */
export const sizeText = (bytes: number): string => {
  if (!bytes || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** "6 Oct 2026". */
export const dateText = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const RANK: Record<string, number> = { OVERDUE: 0, AUTH_FAILED: 1, ERROR: 1, AGEING: 2, NEW: 3, NO_SECRET: 4, PENDING: 5, CLEAR: 6, DISABLED: 7 };

export type SortKey = 'attention' | 'unread' | 'oldest';

/** Overdue and broken first by default; or by unread count, or by the oldest unread. */
export const sortMailboxes = (rows: MailboxRow[], key: SortKey): MailboxRow[] => {
  const copy = [...rows];
  const oldest = (r: MailboxRow) => (r.oldestUnreadAt ? Date.parse(r.oldestUnreadAt) : Number.POSITIVE_INFINITY);
  if (key === 'unread') return copy.sort((a, b) => (b.unread ?? -1) - (a.unread ?? -1) || a.address.localeCompare(b.address));
  if (key === 'oldest') return copy.sort((a, b) => oldest(a) - oldest(b) || a.address.localeCompare(b.address));
  return copy.sort(
    (a, b) =>
      RANK[lookOf(a).key] - RANK[lookOf(b).key] ||
      oldest(a) - oldest(b) ||
      (b.unread ?? 0) - (a.unread ?? 0) ||
      a.address.localeCompare(b.address)
  );
};

export interface MailboxFilter {
  search: string;
  department: string;
  status: '' | 'attention' | 'errors' | 'clear';
  onlyUnread: boolean;
}

export const EMPTY_FILTER: MailboxFilter = { search: '', department: '', status: '', onlyUnread: false };

export const filterMailboxes = (rows: MailboxRow[], f: MailboxFilter): MailboxRow[] => {
  const q = f.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (q && !`${r.address} ${r.displayName} ${r.department ?? ''} ${r.members.map((m) => m.name).join(' ')}`.toLowerCase().includes(q)) {
      return false;
    }
    if (f.department && (r.department ?? '') !== f.department) return false;
    if (f.onlyUnread && !(r.unread && r.unread > 0)) return false;
    const key = lookOf(r).key;
    if (f.status === 'attention' && key !== 'OVERDUE' && key !== 'AGEING') return false;
    if (f.status === 'errors' && key !== 'AUTH_FAILED' && key !== 'ERROR' && key !== 'NO_SECRET') return false;
    if (f.status === 'clear' && key !== 'CLEAR') return false;
    return true;
  });
};

/**
 * Applies a live push to the cached list: the summary is replaced and each changed row is
 * patched in place. Returns null when a pushed id is unknown, so the caller refetches.
 */
export const mergeLiveUpdate = (list: MailboxList | undefined, update: LiveUpdate): MailboxList | null => {
  if (!list) return null;
  const byId = new Map(update.changed.map((c) => [c.mailboxId, c]));
  const known = new Set(list.mailboxes.map((m) => m.id));
  if (update.changed.some((c) => !known.has(c.mailboxId))) return null;
  return {
    summary: update.summary,
    mailboxes: list.mailboxes.map((m) => {
      const c = byId.get(m.id);
      if (!c) return m;
      return {
        ...m,
        unread: c.unread,
        oldestUnreadAt: c.oldestUnreadAt,
        newestUnreadAt: c.newestUnreadAt,
        lastCheckedAt: c.lastCheckedAt,
        status: c.status,
        level: c.level,
      };
    }),
  };
};

/** The highest unread of each of the last `days` days (oldest first), for the trend line. */
export const dailyPeaks = (points: HistoryPoint[], days: number, now: number): number[] => {
  const peaks = new Array<number>(days).fill(0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  for (const p of points) {
    const d = new Date(p.at);
    d.setHours(0, 0, 0, 0);
    const back = Math.round((today.getTime() - d.getTime()) / 86_400_000);
    const index = days - 1 - back;
    if (index >= 0 && index < days) peaks[index] = Math.max(peaks[index], p.unread);
  }
  return peaks;
};

export const initials = (name: string): string =>
  name
    .replace(/\b(A\/L|A\/P|BIN|BINTI)\b/gi, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

export const owners = (row: MailboxRow) => row.members.filter((m) => m.role === 'OWNER' && m.active);
